import { useState } from 'react'
import { useStudioLocale } from '@/i18n'
import type { CompositeAction, CompositeActionItem } from '../services/composite-action'
import { ConversationArtifactCard } from './conversation-artifact-card'

interface ApprovalCardProps {
  compositeActions: CompositeAction[]
  onApply: (items: CompositeActionItem[]) => void
  onDismiss: () => void
  appliedStatus?: 'pass' | 'blocked' | 'missing'
  appliedBlockedReason?: string
}

/**
 * Carryover: this card was the FOURTH hardcoded-English copy the wave-7
 * census found — a surface where a Korean bank operator approves changes to
 * their own project, in English. Labels now come from the session's
 * dictionary, keyed by the same category ids the items carry.
 */
type ApprovalCopy = ReturnType<typeof useStudioLocale>['strings']['approval']

function categoryLabel(copy: ApprovalCopy, category: string): string {
  if (category === 'flow') return copy.categoryFlows
  if (category === 'datasource') return copy.categoryDatasources
  if (category === 'variable') return copy.categoryVariables
  return copy.categoryEvents
}

const categoryIcons: Record<string, string> = {
  flow: '',
  datasource: '',
  variable: '',
  event: '',
}

export function ApprovalCard({
  compositeActions,
  onApply,
  onDismiss,
  appliedStatus,
  appliedBlockedReason,
}: ApprovalCardProps) {
  const copy = useStudioLocale().strings.approval
  const [status, setStatus] = useState<'pending' | 'editing' | 'submitted' | 'applied' | 'dismissed'>('pending')
  const actionDescription = compositeActions[0]?.description?.trim()

  const allItems = compositeActions.flatMap(c => c.items)
  const [enabledItems, setEnabledItems] = useState<boolean[]>(() => allItems.map(() => true))
  const requiresAppliedEvidence = appliedStatus !== undefined
  const appliedEvidencePassed = !requiresAppliedEvidence || appliedStatus === 'pass'
  const completionStatus = status === 'submitted' && appliedEvidencePassed ? 'applied' : status

  const handleApplyAll = () => {
    setStatus(appliedEvidencePassed ? 'applied' : 'submitted')
    onApply(allItems)
  }

  const handleEdit = () => {
    setStatus('editing')
  }

  const handleApplyEdited = () => {
    const selectedItems = allItems.filter((_, i) => enabledItems[i])
    setStatus(appliedEvidencePassed ? 'applied' : 'submitted')
    onApply(selectedItems)
  }

  const handleDismiss = () => {
    setStatus('dismissed')
    onDismiss()
  }

  const toggleItem = (index: number) => {
    setEnabledItems(prev => {
      const next = [...prev]
      next[index] = !next[index]
      return next
    })
  }

  const indexed = allItems.map((item, i) => ({ item, globalIndex: i }))
  const grouped = {
    flow: indexed.filter(({ item }) => item.category === 'flow'),
    datasource: indexed.filter(({ item }) => item.category === 'datasource'),
    variable: indexed.filter(({ item }) => item.category === 'variable'),
    event: indexed.filter(({ item }) => item.category === 'event'),
  }

  return (
    <ConversationArtifactCard
      testId="approval-card"
      className="my-2"
      eyebrow={actionDescription ? copy.compositeActionFallback : undefined}
      title={actionDescription || copy.compositeActionFallback}
      meta={copy.operationCount(allItems.length)}
      preview={(
        <div className="space-y-3">
        {(['flow', 'datasource', 'variable', 'event'] as const).map(cat => {
          const entries = grouped[cat]
          if (entries.length === 0) return null
          return (
            <div key={cat} data-testid={`approval-category-${cat}`}>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {categoryIcons[cat]}{categoryLabel(copy, cat)}
              </div>
              {entries.map(({ item, globalIndex }) => (
                <div
                  key={globalIndex}
                  className={`text-sm pl-5 py-0.5 flex items-center gap-2 ${
                    status === 'editing' && !enabledItems[globalIndex] ? 'opacity-40 line-through' : ''
                  }`}
                  data-testid="approval-item"
                >
                  {status === 'editing' && (
                    <input
                      type="checkbox"
                      checked={enabledItems[globalIndex]}
                      onChange={() => toggleItem(globalIndex)}
                      data-testid={`approval-item-toggle-${globalIndex}`}
                      className="accent-foreground"
                    />
                  )}
                  <span>{item.summary}</span>
                </div>
              ))}
            </div>
          )
        })}
        </div>
      )}
      actions={(
        <>
        {completionStatus === 'pending' ? (
          <>
            <button
              className="inline-flex min-h-11 items-center rounded-md border border-border bg-foreground px-3 text-sm font-medium text-background hover:bg-foreground/90 sm:min-h-9"
              onClick={handleApplyAll}
              data-testid="approval-apply"
            >
              {copy.applyAll}
            </button>
            <button
              className="inline-flex min-h-11 items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-accent sm:min-h-9"
              onClick={handleEdit}
              data-testid="approval-edit"
            >
              {copy.edit}
            </button>
            <button
              className="inline-flex min-h-11 items-center rounded-md border border-border px-3 text-sm font-medium hover:bg-accent sm:min-h-9"
              onClick={handleDismiss}
              data-testid="approval-dismiss"
            >
              {copy.dismiss}
            </button>
          </>
        ) : completionStatus === 'editing' ? (
          <>
            <button
              className="inline-flex min-h-11 items-center rounded-md border border-border bg-foreground px-3 text-sm font-medium text-background hover:bg-foreground/90 sm:min-h-9"
              onClick={handleApplyEdited}
              data-testid="approval-apply-edited"
            >
              {copy.applySelected(enabledItems.filter(Boolean).length, allItems.length)}
            </button>
            <button
              className="inline-flex min-h-11 items-center rounded-md border border-border px-3 text-sm font-medium hover:bg-accent sm:min-h-9"
              onClick={handleDismiss}
              data-testid="approval-dismiss"
            >
              {copy.dismiss}
            </button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="approval-status">
            {completionStatus === 'applied'
              ? copy.applied
              : completionStatus === 'submitted'
                ? copy.pendingValidation(appliedBlockedReason)
                : copy.dismissed}
          </span>
        )}
        </>
      )}
    />
  )
}
